/**
 * FE-xxx 오류 코드 (SRS v0.2 부록 B ErrorCatalog 단일 원천 · SCR v0.6 FS-C1).
 * 셸은 코드만 다루고 문안은 i18n 카탈로그(fe.*)에서 고른다.
 */
export const FE_CODES = [
  "FE-101",
  "FE-102",
  "FE-103",
  "FE-104",
  "FE-201",
  "FE-202",
  "FE-203",
  "FE-204",
  "FE-301",
  "FE-302",
  "FE-303",
  "FE-304",
  "FE-305",
  "FE-306",
  /** 내 작품에 반응(409 · IR-08) — 승인 2026-09-25(SRS 부록 B) */
  "FE-307",
  "FE-401",
  "FE-402",
  "FE-501",
  "FE-502",
  /** 리플레이 512KB 초과(413) · 리플레이 만료(410 · 보존 180일) — 승인 2026-09-25(SRS 부록 B) */
  "FE-503",
  "FE-504",
  "FE-601",
] as const;

export type FeCode = (typeof FE_CODES)[number];

/**
 * 셸 내부 상태 코드 — FE 카탈로그에 없는 전송 계층 실패.
 *  - NET-OFFLINE: 제어 평면에 닿지 못함(네트워크·CORS·DNS)
 *  - NET-SERVER: 5xx 또는 계약 밖 응답
 * 교사에게는 코드 대신 이 이름을 보이고, 학생에게는 '다시 해 보기'만 보인다.
 */
export type ShellCode = "NET-OFFLINE" | "NET-SERVER";

export type ErrorCode = FeCode | ShellCode;

export function isFeCode(v: unknown): v is FeCode {
  return typeof v === "string" && (FE_CODES as readonly string[]).includes(v);
}

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  /** 응답 본문(있으면) — 422 {code, issues} 처럼 코드 외 상세가 필요한 호출부가 읽는다 */
  readonly body: unknown;

  constructor(code: ErrorCode, status: number, message?: string, body?: unknown) {
    super(message ?? code);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.body = body ?? null;
  }

  /** 다시 시도하면 나아질 수 있는 실패(네트워크·서버) */
  get retryable(): boolean {
    return this.code === "NET-OFFLINE" || this.code === "NET-SERVER";
  }
}

/**
 * HTTP 상태 + 본문 → 오류 코드.
 * 본문에 FE 코드가 있으면 그대로 쓰고, 없으면 상태로 추정한다(401 → FE-304 · 403 → FE-302).
 */
export function errorFromResponse(status: number, body: unknown): ApiError {
  const b = (body ?? {}) as { code?: unknown; message?: unknown };
  const message = typeof b.message === "string" ? b.message : undefined;
  if (isFeCode(b.code)) return new ApiError(b.code, status, message, body);
  if (status === 401) return new ApiError("FE-304", status, message, body);
  if (status === 403) return new ApiError("FE-302", status, message, body);
  return new ApiError("NET-SERVER", status, message, body);
}

export function toApiError(e: unknown): ApiError {
  if (e instanceof ApiError) return e;
  return new ApiError("NET-OFFLINE", 0, e instanceof Error ? e.message : String(e));
}
